import {
  PlaycastMessage,
  PlaycastMessageSystemGetAutomaticUpdatesEnabledReply,
  PlaycastMessageSystemGetCurrentUserInfoReply,
  PlaycastMessageSystemGetDriverStatusReply,
  PlaycastMessageSystemGetSanitizationStateReply,
  PlaycastMessageSystemGetRestartRequiredReply,
  PlaycastMessageSystemGetScoringMetricsReply,
  PlaycastMessageSystemUpdateHardwareDatabaseReply,
  PlaycastMessageSystemGetMachineIdReply,
  PlaycastMessageSystemGetMachineIdAttestationReply,
  PlaycastMessageSystemSignMachineIdPayloadReply,
  PlaycastMessageSystemSelfCheckMachineIdReply,
  PlaycastMessageSystemGetUserReply,
  PlaycastMessageSystemSecureStorageGetReply,
  PlaycastMessageSystemSecureStorageSetReply,
  PlaycastMessageSystemSecureStorageRemoveReply,
  PlaycastMessageSystemSetUnattendedConfigurationReply,
  PlaycastMessageSystemSubmitInstallRequestReply,
  PlaycastMessageSystemGetNonsoleReadinessReportReply,
  PlaycastMessageSystemRequestHostModeEntryReply,
  PlaycastMessageSystemPowerTransition,
  PlaycastMessageSystemPowerTransitionAck,
  PlaycastMessageTarget,
} from '../types';
import { PowerTransitionAckPayloadSchema, PowerTransitionPayloadSchema } from '../power/powerEvent';

const TARGET = 'system';

type msg = PlaycastMessage<PlaycastMessageTarget>;
export const getMachineIdReply = (message: msg): PlaycastMessage<PlaycastMessageSystemGetMachineIdReply> | undefined => {
  const target = message.header.target;
  const action = message.header.action;
  const fail = (field: string) => {
    console.error(`Failed to validate message: ${field} is missing`);
    return undefined;
  };

  if (target !== TARGET) {
    console.error(`Failed to validate message: target is not system`);
    return undefined;
  }
  if (action !== 'getMachineId') {
    console.error(`Failed to validate message: action is not getMachineId`);
    return undefined;
  }

  const messageObj = message as PlaycastMessage<PlaycastMessageSystemGetMachineIdReply>;
  const bodyData = messageObj.body.message;
  if (!bodyData) return fail('body.message');
  if (bodyData.errorCode === undefined) return fail('body.message.errorCode');
  if (bodyData.machineId === undefined) return fail('body.message.machineId');
  if (bodyData.isValid === undefined) return fail('body.message.isValid');
  if (bodyData.keyBacking === undefined) return fail('body.message.keyBacking');
  return messageObj;
};

export const signMachineIdPayloadReply = (
  message: msg,
): PlaycastMessage<PlaycastMessageSystemSignMachineIdPayloadReply> | undefined => {
  const target = message.header.target;
  const action = message.header.action;
  const fail = (field: string) => {
    console.error(`Failed to validate message: ${field} is missing`);
    return undefined;
  };

  if (target !== TARGET) {
    console.error(`Failed to validate message: target is not system`);
    return undefined;
  }
  if (action !== 'signMachineIdPayload') {
    console.error(`Failed to validate message: action is not signMachineIdPayload`);
    return undefined;
  }

  const messageObj = message as PlaycastMessage<PlaycastMessageSystemSignMachineIdPayloadReply>;
  const bodyData = messageObj.body.message;
  if (!bodyData) return fail('body.message');
  if (bodyData.errorCode === undefined) return fail('body.message.errorCode');
  if (bodyData.requestId === undefined) return fail('body.message.requestId');
  if (bodyData.errorCode === 'SUCCESS') {
    if (typeof bodyData.signature !== 'string' || bodyData.signature.length === 0) {
      return fail('body.message.signature');
    }
  }
  if (bodyData.errorCode !== 'SUCCESS' && bodyData.error === undefined) {
    return fail('body.message.error');
  }
  return messageObj;
};

export const getMachineIdAttestationReply = (
  message: msg,
): PlaycastMessage<PlaycastMessageSystemGetMachineIdAttestationReply> | undefined => {
  const target = message.header.target;
  const action = message.header.action;
  const fail = (field: string) => {
    console.error(`Failed to validate message: ${field} is missing`);
    return undefined;
  };

  if (target !== TARGET) {
    console.error(`Failed to validate message: target is not system`);
    return undefined;
  }
  if (action !== 'getMachineIdAttestation') {
    console.error(`Failed to validate message: action is not getMachineIdAttestation`);
    return undefined;
  }

  const messageObj = message as PlaycastMessage<PlaycastMessageSystemGetMachineIdAttestationReply>;
  const bodyData = messageObj.body.message;
  if (!bodyData) return fail('body.message');
  if (bodyData.errorCode === undefined) return fail('body.message.errorCode');
  if (bodyData.machineId === undefined) return fail('body.message.machineId');
  if (bodyData.aikPublicKey === undefined) return fail('body.message.aikPublicKey');
  if (bodyData.seedSignature === undefined) return fail('body.message.seedSignature');
  if (bodyData.tpmKeyHandle === undefined) return fail('body.message.tpmKeyHandle');
  if (bodyData.nonceEcho === undefined) return fail('body.message.nonceEcho');
  if (bodyData.timestamp === undefined) return fail('body.message.timestamp');
  if (bodyData.version === undefined) return fail('body.message.version');

  if (bodyData.errorCode === 'SUCCESS') {
    const sig = bodyData.signature;
    if (typeof sig !== 'string' || sig.length === 0) {
      return fail('body.message.signature');
    }
  }
  if (bodyData.errorCode !== 'SUCCESS' && bodyData.error === undefined)
    return fail('body.message.error');

  return messageObj;
};

export const selfCheckMachineIdReply = (
  message: msg,
): PlaycastMessage<PlaycastMessageSystemSelfCheckMachineIdReply> | undefined => {
  const target = message.header.target;
  const action = message.header.action;
  const fail = (field: string) => {
    console.error(`Failed to validate message: ${field} is missing`);
    return undefined;
  };

  if (target !== TARGET) {
    console.error(`Failed to validate message: target is not system`);
    return undefined;
  }
  if (action !== 'selfCheckMachineId') {
    console.error(`Failed to validate message: action is not selfCheckMachineId`);
    return undefined;
  }

  const messageObj = message as PlaycastMessage<PlaycastMessageSystemSelfCheckMachineIdReply>;
  const bodyData = messageObj.body.message;
  if (!bodyData) return fail('body.message');
  if (bodyData.status === undefined) return fail('body.message.status');
  if (!Array.isArray(bodyData.checks)) return fail('body.message.checks');
  if (bodyData.machineId === undefined) return fail('body.message.machineId');
  if (bodyData.tpmPresent === undefined) return fail('body.message.tpmPresent');
  if (bodyData.attestationTokenConfigured === undefined)
    return fail('body.message.attestationTokenConfigured');

  return messageObj;
};

export const getCurrentUserInfoReply = (message: msg): PlaycastMessage<PlaycastMessageSystemGetCurrentUserInfoReply> | undefined => {
  const target = message.header.target;
  const action = message.header.action;
  const fail = (field: string) => {
    console.error(`Failed to validate message: ${field} is missing`);
    return undefined;
  };

  if (target !== TARGET) {
    console.error(`Failed to validate message: target is not system`);
    return undefined;
  }
  if (action !== 'getCurrentUserInfo') {
    console.error(`Failed to validate message: action is not getCurrentUserInfo`);
    return undefined;
  }

  const messageObj = message as PlaycastMessage<PlaycastMessageSystemGetCurrentUserInfoReply>;
  const bodyData = messageObj.body.message;
  // Check required fields.
  if (bodyData.active === undefined) return fail('body.message.active');
  if (bodyData.unique === undefined) return fail('body.message.unique');
  if (bodyData.username === undefined) return fail('body.message.username');
  if (bodyData.loggedIn === undefined) return fail('body.message.loggedIn');
  if (bodyData.privilege === undefined) return fail('body.message.privilege');
  return messageObj;
};

export const getDriverStatusReply = (message: msg): PlaycastMessage<PlaycastMessageSystemGetDriverStatusReply> | undefined => {
  const target = message.header.target;
  const action = message.header.action;
  const fail = (field: string) => {
    console.error(`Failed to validate message: ${field} is missing`);
    return undefined;
  };

  if (target !== TARGET) {
    console.error(`Failed to validate message: target is not system`);
    return undefined;
  }
  if (action !== 'getDriverStatus') {
    console.error(`Failed to validate message: action is not getDriverStatus`);
    return undefined;
  }

  const messageObj = message as PlaycastMessage<PlaycastMessageSystemGetDriverStatusReply>;
  const bodyData = messageObj.body.message;
  // Check required fields.
  if (bodyData.ShadowCam00 === undefined) return fail('body.message.ShadowCam00');
  if (bodyData.ShadowCam01 === undefined) return fail('body.message.ShadowCam01');
  if (bodyData.ShadowCam02 === undefined) return fail('body.message.ShadowCam02');
  if (bodyData.ShadowCam03 === undefined) return fail('body.message.ShadowCam03');
  if (bodyData.ShadowCam04 === undefined) return fail('body.message.ShadowCam04');
  if (bodyData.ShadowCam05 === undefined) return fail('body.message.ShadowCam05');
  if (bodyData.ShadowCam06 === undefined) return fail('body.message.ShadowCam06');
  if (bodyData.ShadowCam07 === undefined) return fail('body.message.ShadowCam07');
  if (bodyData.ShadowCam08 === undefined) return fail('body.message.ShadowCam08');
  if (bodyData.audioSource === undefined) return fail('body.message.audioSource');
  if (bodyData.displayManager === undefined) return fail('body.message.displayManager');
  if (bodyData.gameController === undefined) return fail('body.message.gameController');
  if (bodyData.shadowKeyboard === undefined) return fail('body.message.shadowKeyboard');
  if (bodyData.shadowMouse === undefined) return fail('body.message.shadowMouse');
  if (bodyData.inputCasterMouse === undefined) return fail('body.message.inputCasterMouse');
  if (bodyData.inputCasterKeyboard === undefined) return fail('body.message.inputCasterKeyboard');
  if (bodyData.filterDriverMouse === undefined) return fail('body.message.filterDriverMouse');
  if (bodyData.filterDriverKeyboard === undefined) return fail('body.message.filterDriverKeyboard');
  if (bodyData.touch === undefined) return fail('body.message.touch');
  if (bodyData.shadowDisplay === undefined) return fail('body.message.shadowDisplay');
  return messageObj;
};

export const getSanitizationStateReply = (
  message: msg,
): PlaycastMessage<PlaycastMessageSystemGetSanitizationStateReply> | undefined => {
  const target = message.header.target;
  const action = message.header.action;
  const fail = (field: string) => {
    console.error(`Failed to validate message: ${field} is missing`);
    return undefined;
  };

  if (target !== TARGET) {
    console.error(`Failed to validate message: target is not system`);
    return undefined;
  }
  if (action !== 'getSanitizationState') {
    console.error(`Failed to validate message: action is not getSanitizationState`);
    return undefined;
  }

  const failType = (field: string) => {
    console.error(`Failed to validate message: ${field} has the wrong type`);
    return undefined;
  };

  const messageObj = message as PlaycastMessage<PlaycastMessageSystemGetSanitizationStateReply>;
  const bodyData = messageObj.body.message;
  if (!bodyData) return fail('body.message');
  if (bodyData.isBetaHost === undefined) return fail('body.message.isBetaHost');
  if (bodyData.sanitized === undefined) return fail('body.message.sanitized');
  if (bodyData.processPolicyHealthy === undefined) return fail('body.message.processPolicyHealthy');
  if (bodyData.virtualDisplayHealthy === undefined) return fail('body.message.virtualDisplayHealthy');
  if (bodyData.physicalIsolationHealthy === undefined) return fail('body.message.physicalIsolationHealthy');
  // Backward-compatible: older Playjector builds omit hostAudioHealthy. Requiring it would
  // reject the whole reply during version skew and clear sanitizationState to {}, which the
  // monitor reads as non-beta/sanitized — silently dropping the matchmaking gate. Accept when
  // absent (audio sub-health simply unknown; C++-computed `sanitized` still gates), but when
  // present it must be a boolean so malformed payloads are still caught.
  if (bodyData.hostAudioHealthy !== undefined && typeof bodyData.hostAudioHealthy !== 'boolean') {
    return failType('body.message.hostAudioHealthy');
  }
  if (bodyData.failureCode === undefined) return fail('body.message.failureCode');
  if (bodyData.failureDetail === undefined) return fail('body.message.failureDetail');
  if (
    bodyData.hostAudioHideFailureReason !== undefined &&
    typeof bodyData.hostAudioHideFailureReason !== 'string'
  ) {
    return failType('body.message.hostAudioHideFailureReason');
  }
  if (bodyData.lastUpdatedUtcMs === undefined) return fail('body.message.lastUpdatedUtcMs');
  // Observe-only violation telemetry — optional for backward compatibility, but
  // when present must be well-typed so malformed payloads are caught here rather
  // than leaking into matchmaking / admin state flows. Mirrors HostSanitizationState.
  if (bodyData.processPolicyViolationCount !== undefined) {
    if (
      typeof bodyData.processPolicyViolationCount !== 'number' ||
      !Number.isInteger(bodyData.processPolicyViolationCount) ||
      bodyData.processPolicyViolationCount < 0
    ) {
      return failType('body.message.processPolicyViolationCount');
    }
  }
  if (bodyData.lastBlockedProcessName !== undefined && typeof bodyData.lastBlockedProcessName !== 'string') {
    return failType('body.message.lastBlockedProcessName');
  }
  if (bodyData.lastBlockedReason !== undefined && typeof bodyData.lastBlockedReason !== 'string') {
    return failType('body.message.lastBlockedReason');
  }
  return messageObj;
};

export const getRestartRequiredReply = (
  message: msg,
): PlaycastMessage<PlaycastMessageSystemGetRestartRequiredReply> | undefined => {
  const target = message.header.target;
  const action = message.header.action;
  const fail = (field: string) => {
    console.error(`Failed to validate message: ${field} is missing`);
    return undefined;
  };

  if (target !== TARGET) {
    console.error(`Failed to validate message: target is not system`);
    return undefined;
  }
  if (action !== 'getRestartRequired') {
    console.error(`Failed to validate message: action is not getRestartRequired`);
    return undefined;
  }

  const messageObj = message as PlaycastMessage<PlaycastMessageSystemGetRestartRequiredReply>;
  const bodyData = messageObj.body.message;
  if (!bodyData) return fail('body.message');
  if (bodyData.restartRequired === undefined) return fail('body.message.restartRequired');
  if (bodyData.restartRequiredReason === undefined) return fail('body.message.restartRequiredReason');
  return messageObj;
};

export const getUserReply = (message: msg): PlaycastMessage<PlaycastMessageSystemGetUserReply> | undefined => {
  const target = message.header.target;
  const action = message.header.action;
  const fail = (field: string) => {
    console.error(`Failed to validate message: ${field} is missing`);
    return undefined;
  };

  if (target !== TARGET) {
    console.error(`Failed to validate message: target is not system`);
    return undefined;
  }
  if (action !== 'getUser') {
    console.error(`Failed to validate message: action is not getUser`);
    return undefined;
  }

  const messageObj = message as PlaycastMessage<PlaycastMessageSystemGetUserReply>;
  const bodyData = messageObj.body.message;
  // Check required fields.
  if (bodyData.active === undefined) return fail('body.message.active');
  if (bodyData.unique === undefined) return fail('body.message.unique');
  if (bodyData.username === undefined) return fail('body.message.username');
  if (bodyData.loggedIn === undefined) return fail('body.message.loggedIn');
  if (bodyData.privilege === undefined) return fail('body.message.privilege');
  return messageObj;
};

export const getAutomaticUpdatesEnabledReply = (
  message: PlaycastMessage<PlaycastMessageTarget>,
): PlaycastMessage<PlaycastMessageSystemGetAutomaticUpdatesEnabledReply> | undefined => {
  const target = message.header.target;
  const action = message.header.action;
  const fail = (field: string) => {
    console.error(`Failed to validate message: ${field} is missing`);
    return undefined;
  };

  if (target !== 'system') {
    console.error(`Failed to validate message: target is not system`);
    return undefined;
  }
  if (action !== 'getAutomaticUpdatesEnabled') {
    console.error(`Failed to validate message: action is not getAutomaticUpdatesEnabled`);
    return undefined;
  }

  const messageObj = message as PlaycastMessage<PlaycastMessageSystemGetAutomaticUpdatesEnabledReply>;

  const bodyData = messageObj.body.message;
  // Check required fields.
  if (bodyData === undefined) return fail('body.message');
  if (bodyData.automaticUpdatesEnabled === undefined) return fail('body.message.automaticUpdatesEnabled');

  return messageObj;
};

export const updateHardwareDatabaseReply = (
  message: PlaycastMessage<PlaycastMessageTarget>,
): PlaycastMessage<PlaycastMessageSystemUpdateHardwareDatabaseReply> | undefined => {
  const target = message.header.target;
  const action = message.header.action;
  const fail = (field: string) => {
    console.error(`Failed to validate message: ${field} is missing`);
    return undefined;
  };

  if (target !== TARGET) {
    console.error(`Failed to validate message: target is not system`);
    return undefined;
  }
  if (action !== 'updateHardwareDatabase') {
    console.error(`Failed to validate message: action is not updateHardwareDatabase`);
    return undefined;
  }

  const messageObj = message as PlaycastMessage<PlaycastMessageSystemUpdateHardwareDatabaseReply>;
  const bodyData = messageObj.body.message;
  if (!bodyData) return fail('body.message');
  if (bodyData.cpuUpdated === undefined) return fail('body.message.cpuUpdated');
  if (bodyData.gpuUpdated === undefined) return fail('body.message.gpuUpdated');
  if (bodyData.error === undefined) return fail('body.message.error');

  return messageObj;
};

/**
 * Validates the raw-metrics IPC reply produced by Playjector. Only the
 * `allMetrics` bundle and `timestamp` are required; the rest is permissive
 * to keep the Playjector producer loose (TS owns scoring).
 */
export const getScoringMetricsReply = (
  message: PlaycastMessage<PlaycastMessageTarget>,
): PlaycastMessage<PlaycastMessageSystemGetScoringMetricsReply> | undefined => {
  const target = message.header.target;
  const action = message.header.action;
  const fail = (field: string) => {
    console.error(`Failed to validate message: ${field} is missing`);
    return undefined;
  };
  if (target !== TARGET) return fail('header.target');
  if (action !== 'getScoringMetrics') return fail('header.action');
  const messageObj = message as PlaycastMessage<PlaycastMessageSystemGetScoringMetricsReply>;
  const bodyData = messageObj.body.message;
  if (bodyData === undefined) return fail('body.message');
  if (bodyData.allMetrics === undefined || typeof bodyData.allMetrics !== 'object') {
    return fail('body.message.allMetrics');
  }
  if (typeof bodyData.timestamp !== 'number') return fail('body.message.timestamp');
  return messageObj;
};

/**
 * Validate an unsolicited `powerTransition` envelope (PlaycastService -> node-service).
 * Uses the shared Zod payload schema so the shape matches the contract exactly.
 */
export const powerTransition = (
  message: PlaycastMessage<PlaycastMessageTarget>,
): PlaycastMessage<PlaycastMessageSystemPowerTransition> | undefined => {
  const fail = (field: string) => {
    console.error(`Failed to validate message: ${field} is invalid`);
    return undefined;
  };
  // Guard the envelope shape before touching header fields: an untrusted IPC
  // envelope may omit `header` entirely, so read it defensively and route the
  // missing case through fail() rather than letting the property access throw.
  if (!message || !message.header) return fail('header');
  const target = message.header.target;
  const action = message.header.action;
  if (target !== TARGET) return fail('header.target');
  if (action !== 'powerTransition') return fail('header.action');
  // powerTransition is an unsolicited hub->client push. In PlayJector such pushes
  // are tagged isReply=true (the flag that makes the hub forward them out to socket
  // clients; see SchemaList.cpp systemPowerTransitionMSG and hubInitializedMSG).
  // Require it so a malformed envelope with the wrong shape is rejected.
  if (!message.header.isReply) return fail('header.isReply');
  const messageObj = message as PlaycastMessage<PlaycastMessageSystemPowerTransition>;
  // Guard body access: an untrusted envelope may omit `body` entirely, so read
  // it defensively and route the missing case through fail() rather than letting
  // the property access throw.
  const body = messageObj.body as { message?: unknown } | undefined;
  if (body === undefined || body.message === undefined) return fail('body.message');
  const parsed = PowerTransitionPayloadSchema.safeParse(body.message);
  if (!parsed.success) return fail('body.message');
  return messageObj;
};

/**
 * Validate a `powerTransitionAck` envelope (node-service -> PlaycastService).
 */
export const powerTransitionAck = (
  message: PlaycastMessage<PlaycastMessageTarget>,
): PlaycastMessage<PlaycastMessageSystemPowerTransitionAck> | undefined => {
  const fail = (field: string) => {
    console.error(`Failed to validate message: ${field} is invalid`);
    return undefined;
  };
  if (!message || !message.header) return fail('header');
  const target = message.header.target;
  const action = message.header.action;
  if (target !== TARGET) return fail('header.target');
  if (action !== 'powerTransitionAck') return fail('header.action');
  if (message.header.isReply) return fail('header.isReply');
  const messageObj = message as PlaycastMessage<PlaycastMessageSystemPowerTransitionAck>;
  const body = messageObj.body as { message?: unknown } | undefined;
  if (body === undefined || body.message === undefined) return fail('body.message');
  const parsed = PowerTransitionAckPayloadSchema.safeParse(body.message);
  if (!parsed.success) return fail('body.message');
  return messageObj;
};

export const secureStorageGetReply = (
  message: PlaycastMessage<PlaycastMessageTarget>,
): PlaycastMessage<PlaycastMessageSystemSecureStorageGetReply> | undefined => {
  const target = message.header.target;
  const action = message.header.action;
  const fail = (field: string) => {
    console.error(`Failed to validate message: ${field} is missing`);
    return undefined;
  };

  if (target !== TARGET) {
    console.error(`Failed to validate message: target is not system`);
    return undefined;
  }
  if (action !== 'secureStorageGet') {
    console.error(`Failed to validate message: action is not secureStorageGet`);
    return undefined;
  }

  const messageObj = message as PlaycastMessage<PlaycastMessageSystemSecureStorageGetReply>;
  const bodyData = messageObj.body.message;

  // Check required fields.
  if (bodyData === undefined) return fail('body.message');
  if (bodyData.key === undefined) return fail('body.message.key');
  if (bodyData.succeeded === undefined) return fail('body.message.succeeded');
  if (bodyData.value === undefined) return fail('body.message.value');

  return messageObj;
};

export const secureStorageSetReply = (
  message: PlaycastMessage<PlaycastMessageTarget>,
): PlaycastMessage<PlaycastMessageSystemSecureStorageSetReply> | undefined => {
  const target = message.header.target;
  const action = message.header.action;
  const fail = (field: string) => {
    console.error(`Failed to validate message: ${field} is missing`);
    return undefined;
  };

  if (target !== TARGET) {
    console.error(`Failed to validate message: target is not system`);
    return undefined;
  }
  if (action !== 'secureStorageSet') {
    console.error(`Failed to validate message: action is not secureStorageSet`);
    return undefined;
  }

  const messageObj = message as PlaycastMessage<PlaycastMessageSystemSecureStorageSetReply>;
  const bodyData = messageObj.body.message;

  // Check required fields.
  if (bodyData === undefined) return fail('body.message');
  if (bodyData.key === undefined) return fail('body.message.key');
  if (bodyData.succeeded === undefined) return fail('body.message.succeeded');

  return messageObj;
};

export const setUnattendedConfigurationReply = (
  message: PlaycastMessage<PlaycastMessageTarget>,
): PlaycastMessage<PlaycastMessageSystemSetUnattendedConfigurationReply> | undefined => {
  const target = message.header.target;
  const action = message.header.action;
  const fail = (field: string) => {
    console.error(`Failed to validate message: ${field} is missing`);
    return undefined;
  };

  if (target !== TARGET) {
    console.error(`Failed to validate message: target is not system`);
    return undefined;
  }
  if (action !== 'setUnattendedConfiguration') {
    console.error(`Failed to validate message: action is not setUnattendedConfiguration`);
    return undefined;
  }
  if (!message.header.isReply) {
    console.error(`Failed to validate message: isReply is not true`);
    return undefined;
  }

  const messageObj = message as PlaycastMessage<PlaycastMessageSystemSetUnattendedConfigurationReply>;
  const bodyData = messageObj.body.message;

  if (bodyData === undefined) return fail('body.message');
  if (bodyData.accepted === undefined) return fail('body.message.accepted');
  if (bodyData.deliveryId === undefined) return fail('body.message.deliveryId');
  if (bodyData.enabled === undefined) return fail('body.message.enabled');
  if (bodyData.rolloutState === undefined) return fail('body.message.rolloutState');

  return messageObj;
};

export const secureStorageRemoveReply = (
  message: PlaycastMessage<PlaycastMessageTarget>,
): PlaycastMessage<PlaycastMessageSystemSecureStorageRemoveReply> | undefined => {
  const target = message.header.target;
  const action = message.header.action;
  const fail = (field: string) => {
    console.error(`Failed to validate message: ${field} is missing`);
    return undefined;
  };

  if (target !== TARGET) {
    console.error(`Failed to validate message: target is not system`);
    return undefined;
  }
  if (action !== 'secureStorageRemove') {
    console.error(`Failed to validate message: action is not secureStorageRemove`);
    return undefined;
  }

  const messageObj = message as PlaycastMessage<PlaycastMessageSystemSecureStorageRemoveReply>;
  const bodyData = messageObj.body.message;

  // Check required fields.
  if (bodyData === undefined) return fail('body.message');
  if (bodyData.key === undefined) return fail('body.message.key');
  if (bodyData.succeeded === undefined) return fail('body.message.succeeded');

  return messageObj;
};

export const submitInstallRequestReply = (
  message: PlaycastMessage<PlaycastMessageTarget>,
): PlaycastMessage<PlaycastMessageSystemSubmitInstallRequestReply> | undefined => {
  const target = message.header.target;
  const action = message.header.action;
  const fail = (field: string) => {
    console.error(`Failed to validate message: ${field} is missing`);
    return undefined;
  };

  if (target !== TARGET) {
    console.error(`Failed to validate message: target is not system`);
    return undefined;
  }
  if (action !== 'submitInstallRequest') {
    console.error(`Failed to validate message: action is not submitInstallRequest`);
    return undefined;
  }

  const messageObj = message as PlaycastMessage<PlaycastMessageSystemSubmitInstallRequestReply>;
  const bodyData = messageObj.body.message;

  if (bodyData === undefined) return fail('body.message');
  if (bodyData.submitted === undefined) return fail('body.message.submitted');
  if (bodyData.errorCode === undefined) return fail('body.message.errorCode');

  return messageObj;
};

export const getNonsoleReadinessReportReply = (
  message: PlaycastMessage<PlaycastMessageTarget>,
): PlaycastMessage<PlaycastMessageSystemGetNonsoleReadinessReportReply> | undefined => {
  if (!message?.header) return undefined;
  const target = message.header.target;
  const action = message.header.action;
  const fail = (field: string) => {
    console.error(`Failed to validate message: ${field} is missing`);
    return undefined;
  };

  if (target !== TARGET) {
    console.error(`Failed to validate message: target is not system`);
    return undefined;
  }
  if (action !== 'getNonsoleReadinessReport') {
    console.error(`Failed to validate message: action is not getNonsoleReadinessReport`);
    return undefined;
  }

  const messageObj = message as PlaycastMessage<PlaycastMessageSystemGetNonsoleReadinessReportReply>;
  const bodyData = messageObj.body.message;

  if (bodyData === undefined) return fail('body.message');
  if (bodyData.report === undefined && bodyData.error === undefined) return fail('body.message.report or body.message.error');
  if (bodyData.report !== undefined) {
    if ((bodyData.report as any).schemaVersion !== 2) return fail('body.message.report.schemaVersion === 2');
    if (!(bodyData.report as any).machineId) return fail('body.message.report.machineId');
    if (!Array.isArray((bodyData.report as any).gates)) return fail('body.message.report.gates (array)');
  }

  return messageObj;
};

export const requestHostModeEntryReply = (
  message: PlaycastMessage<PlaycastMessageTarget>,
): PlaycastMessage<PlaycastMessageSystemRequestHostModeEntryReply> | undefined => {
  if (!message?.header) return undefined;
  const target = message.header.target;
  const action = message.header.action;
  const fail = (field: string) => {
    console.error(`Failed to validate message: ${field} is missing`);
    return undefined;
  };

  if (target !== TARGET) {
    console.error(`Failed to validate message: target is not system`);
    return undefined;
  }
  if (action !== 'requestHostModeEntry') {
    console.error(`Failed to validate message: action is not requestHostModeEntry`);
    return undefined;
  }

  const messageObj = message as PlaycastMessage<PlaycastMessageSystemRequestHostModeEntryReply>;
  const bodyData = messageObj.body.message;

  if (bodyData === undefined) return fail('body.message');
  if (typeof bodyData.accepted !== 'boolean') return fail('body.message.accepted (boolean)');

  return messageObj;
};
